import React, { useCallback } from 'react';
import "./MessageLinkCard.scss";
import { localize } from 'src/l10n';
import Bookmark from 'src/social-feed/components/SocialBookmark/Bookmark';
import { UnstyledButton } from 'src/ui';
import { decodeHtmlEntities } from 'src/utils';
import Visage2Icon from 'src/visage2/Visage2Icon/Visage2Icon';
import { IMessageLinkCardMetadata } from './useMessageLinkCard';

interface IProps {
    metadata: IMessageLinkCardMetadata;

    /**
     * Shows a button that dismisses the card. Only used by the composer - a
     * sent message's card can't be removed without editing the message.
     */
    onRemove?: () => void;
}

const MessageLinkCard = (props: IProps) => {
    const openUrl = useCallback(() => {
        window.open(props.metadata.url, "_blank", "noopener,noreferrer");
    }, [props.metadata.url]);

    const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        // Bookmark only anchors the domain, title and description. This makes
        // the rest of the card clickable as well, without nesting anchors.
        if ((event.target as HTMLElement).closest("a")) {
            return;
        }

        openUrl();
    }, [openUrl]);

    const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) {
            return;
        }

        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        event.preventDefault();
        openUrl();
    }, [openUrl]);

    return (
        <div className={"MessageLinkCard"}>
            {/*
                The SocialBookmark class is required, not decorative:
                SocialBookmark.scss nests .standard as a descendant of
                .SocialBookmark/.bookmark, while Bookmark puts "standard" on its
                own root element. Without an ancestor carrying one of those
                classes the card loses its padding and image sizing. The feed
                wraps in .SocialBookmark and the composer in .bookmark for the
                same reason.
            */}
            <div
                className={"SocialBookmark"}
                onClick={onClick}
                onKeyDown={onKeyDown}
                role="link"
                tabIndex={0}
                title={localize("BesokLank")}>
                <Bookmark
                    text={props.metadata.title}
                    bookmarkImage={props.metadata.image}
                    bookmarkDescription={decodeHtmlEntities(props.metadata.description)}
                    bookmarkTitle={props.metadata.title}
                    bookmarkType={1}
                    bookmarkUrl={props.metadata.url} />
            </div>
            {props.onRemove && (
                // A sibling of the card rather than a child, so the remove
                // button isn't an interactive element nested inside one.
                <UnstyledButton
                    className={"close-button"}
                    onClick={props.onRemove}
                    title={localize("TaBort")}>
                    <Visage2Icon icon="close-circle" />
                </UnstyledButton>
            )}
        </div>
    )
}

export default MessageLinkCard;
